import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, PlayCircle, Calendar, Mic, Video as VideoIcon, User } from "lucide-react";
import { getIcon } from "@/lib/icons";
import { mediaUrl } from "@/lib/api";
import { getYouTubeEmbedUrl, plainText } from "@/lib/content";
import { RichTextContent } from "@/components/blocks/RichTextContent";

const cardBase =
  "group relative overflow-hidden border border-border bg-card transition-transform duration-300 hover:-translate-y-1";

// --- Evangelism Activity Card ---
export const ActivityCard = ({ activity, className = "" }) => {
  const Icon = getIcon(activity.icon);
  return (
    <Link
      to={`/evangelism#${activity.slug}`}
      data-testid={`activity-card-${activity.slug}`}
      className={`group relative flex min-h-[260px] flex-col justify-end overflow-hidden bg-navy p-6 transition-transform duration-300 hover:-translate-y-1 ${className}`}
    >
      {activity.image && (
        <>
          <img
            src={mediaUrl(activity.image)}
            alt={activity.title}
            className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-navy/20" />
        </>
      )}
      <div className="relative">
        <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-sm bg-brand-orange text-white">
          <Icon className="h-5 w-5" />
        </span>
        <h3 className="font-display text-xl font-semibold leading-snug text-white">{activity.title}</h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-white/70">{activity.description}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-brand-orange">
          Learn more <ArrowUpRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
};

// --- Department Card ---
export const DepartmentCard = ({ dept }) => {
  const Icon = getIcon(dept.icon);
  return (
    <div data-testid={`department-card-${dept.slug}`} className={`${cardBase} p-7`}>
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-navy text-brand-orange">
        <Icon className="h-6 w-6" />
      </span>
      <h3 className="mt-5 font-display text-lg font-semibold text-navy">{dept.name}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{dept.description}</p>
    </div>
  );
};

// --- Leader Card ---
export const LeaderCard = ({ leader }) => (
  <div data-testid={`leader-card-${leader.id}`} className={`${cardBase}`}>
    <div className="aspect-[4/5] w-full bg-navy-surface">
      {leader.image ? (
        <img src={mediaUrl(leader.image)} alt={leader.name} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-white/25">
          <User className="h-16 w-16" />
        </div>
      )}
    </div>
    <div className="p-5">
      <h3 className="font-display text-lg font-semibold text-navy">{leader.name}</h3>
      <p className="mt-1 text-sm font-medium text-brand-orange">{leader.role}</p>
      {leader.bio && (
        <RichTextContent
          className="mt-3 text-sm leading-relaxed text-muted-foreground"
          html={leader.bio}
          testId={`leader-bio-${leader.id}`}
        />
      )}
    </div>
  </div>
);

// --- Sermon Card ---
export const SermonCard = ({ sermon }) => {
  const embedUrl = getYouTubeEmbedUrl(sermon);
  const sermonId = sermon.id || sermon.youtube_id || sermon.title;

  return (
    <div data-testid={`sermon-card-${sermonId}`} className={`${cardBase}`}>
      <div className="relative aspect-video w-full bg-navy-surface">
        {embedUrl ? (
          <iframe
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
            data-testid={`sermon-embed-${sermonId}`}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            src={embedUrl}
            title={sermon.title}
          />
        ) : (
          <>
            {(sermon.thumbnail || sermon.image) && (
              <img
                src={mediaUrl(sermon.thumbnail || sermon.image)}
                alt={sermon.title}
                className="h-full w-full object-cover"
              />
            )}
            <span className="absolute inset-0 flex items-center justify-center text-white/80">
              <PlayCircle className="h-12 w-12" />
            </span>
          </>
        )}
      </div>
      <div className="p-5">
        {sermon.speaker && (
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-orange">
            {sermon.speaker}
          </p>
        )}
        <h3 className="mt-1 font-display text-lg font-semibold text-navy">{sermon.title}</h3>
        {sermon.description && (
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {plainText(sermon.description)}
          </p>
        )}
        {sermon.date && <p className="mt-2 text-xs text-muted-foreground">{sermon.date}</p>}
      </div>
    </div>
  );
};

// --- Podcast Card ---
export const PodcastCard = ({ podcast }) => (
  <div data-testid={`podcast-card-${podcast.id}`} className={`${cardBase} flex items-center gap-4 p-5`}>
    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-sm bg-navy text-brand-orange">
      <Mic className="h-6 w-6" />
    </span>
    <div>
      <h3 className="font-display text-base font-semibold text-navy">{podcast.title}</h3>
      {podcast.description && (
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
          {plainText(podcast.description)}
        </p>
      )}
    </div>
  </div>
);

// --- Event Card ---
export const EventCard = ({ event }) => (
  <div data-testid={`event-card-${event.id}`} className={`${cardBase} flex gap-5 p-6`}>
    <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-sm bg-navy text-white">
      <Calendar className="h-5 w-5 text-brand-orange" />
    </div>
    <div>
      <h3 className="font-display text-lg font-semibold text-navy">{event.title}</h3>
      {event.date && <p className="mt-1 text-sm text-brand-orange">{event.date}</p>}
      {event.location && <p className="text-sm text-muted-foreground">{event.location}</p>}
      {event.description && (
        <RichTextContent
          className="mt-2 text-sm leading-relaxed text-muted-foreground"
          html={event.description}
          testId={`event-description-${event.id}`}
        />
      )}
    </div>
  </div>
);

// --- Video Card ---
export const VideoCard = ({ video }) => {
  const embedUrl = getYouTubeEmbedUrl(video);
  const videoId = video.id || video.youtube_id || video.title;

  return (
    <div data-testid={`video-card-${videoId}`} className={`${cardBase}`}>
      <div className="relative aspect-video w-full bg-navy-surface">
        {embedUrl ? (
          <iframe
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
            data-testid={`video-embed-${videoId}`}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            src={embedUrl}
            title={video.title}
          />
        ) : (
          <>
            {video.thumbnail && (
              <img
                src={mediaUrl(video.thumbnail)}
                alt={video.title}
                className="h-full w-full object-cover"
              />
            )}
            <span className="absolute inset-0 flex items-center justify-center text-white/80">
              <VideoIcon className="h-12 w-12" />
            </span>
          </>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display text-base font-semibold text-navy">{video.title}</h3>
        {video.description && (
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {plainText(video.description)}
          </p>
        )}
      </div>
    </div>
  );
};

// --- Gallery Card ---
export const GalleryCard = ({ item, onOpen, testId }) => (
  <button
    aria-label={`Open ${plainText(item.caption) || "gallery image"}`}
    className="group relative aspect-square overflow-hidden border border-border bg-navy-surface text-left"
    data-testid={testId || `gallery-open-${item.id}`}
    onClick={onOpen}
    type="button"
  >
    {item.image && (
      <img
        src={mediaUrl(item.image)}
        alt={plainText(item.caption) || "Gallery"}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    )}
    {item.caption && (
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/90 to-transparent p-4">
        <p className="text-sm text-white">{plainText(item.caption)}</p>
      </div>
    )}
  </button>
);
